import "./App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Home from "./pages/Home"; // Home eager-loaded for fastest LCP
import ChatWidget from "./components/ChatWidget";
import { Toaster } from "./components/ui/sonner";
import { initAttribution, track } from "./lib/analytics";
import { registerWebMCPTools } from "./lib/webmcp";

// Lazy-loaded routes (reduces initial JS bundle ~60-70%)
const Services = lazy(() => import("./pages/Services"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Showroom = lazy(() => import("./pages/Showroom"));
const GarageFloorCoating = lazy(() => import("./pages/GarageFloorCoating"));
const MetallicEpoxy = lazy(() => import("./pages/MetallicEpoxy"));
const FlakeSystem = lazy(() => import("./pages/FlakeSystem"));
const QuartzSystem = lazy(() => import("./pages/QuartzSystem"));
const PoolDeckResurfacing = lazy(() => import("./pages/PoolDeckResurfacing"));
const CommercialEpoxy = lazy(() => import("./pages/CommercialEpoxy"));
const IndustrialEpoxy = lazy(() => import("./pages/IndustrialEpoxy"));
const ConcreteRepair = lazy(() => import("./pages/ConcreteRepair"));
const DrivewayResurfacing = lazy(() => import("./pages/DrivewayResurfacing"));
const Waterproofing = lazy(() => import("./pages/Waterproofing"));
const UrethaneCement = lazy(() => import("./pages/UrethaneCement"));
const GovernmentContracts = lazy(() => import("./pages/GovernmentContracts"));
const EpoxyFlooringNaples = lazy(() => import("./pages/EpoxyFlooringNaples"));
const EpoxyFlooringFortMyers = lazy(() => import("./pages/EpoxyFlooringFortMyers"));
const EpoxyFlooringCapeCoral = lazy(() => import("./pages/EpoxyFlooringCapeCoral"));
const EpoxyFlooringMarcoIsland = lazy(() => import("./pages/EpoxyFlooringMarcoIsland"));
const EpoxyFlooringBonitaSprings = lazy(() => import("./pages/EpoxyFlooringBonitaSprings"));
const EpoxyFlooringEstero = lazy(() => import("./pages/EpoxyFlooringEstero"));
const EpoxyVsPolyaspartic = lazy(() => import("./pages/EpoxyVsPolyaspartic"));
const WhyEpoxyArt33 = lazy(() => import("./pages/WhyEpoxyArt33"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Products = lazy(() => import("./pages/Products"));
const FloorVisualizer = lazy(() => import("./pages/FloorVisualizer"));
const Warranty = lazy(() => import("./pages/Warranty"));
const ConcretePolishing = lazy(() => import("./pages/ConcretePolishing"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const EpoxyFlooringSarasota = lazy(() => import("./pages/EpoxyFlooringSarasota"));
const EpoxyFlooringVenice = lazy(() => import("./pages/EpoxyFlooringVenice"));
const EpoxyFlooringNorthPort = lazy(() => import("./pages/EpoxyFlooringNorthPort"));
const EpoxyFlooringPortCharlotte = lazy(() => import("./pages/EpoxyFlooringPortCharlotte"));
const EpoxyFlooringPuntaGorda = lazy(() => import("./pages/EpoxyFlooringPuntaGorda"));
const EpoxyFlooringRotondaWest = lazy(() => import("./pages/EpoxyFlooringRotondaWest"));
const ResidentialHub = lazy(() => import("./pages/ResidentialHub"));
const CommercialHub = lazy(() => import("./pages/CommercialHub"));
const SystemsHub = lazy(() => import("./pages/SystemsHub"));
const IndustriesHub = lazy(() => import("./pages/IndustriesHub"));
const EpoxyFlooringLakewoodRanch = lazy(() => import("./pages/EpoxyFlooringLakewoodRanch"));
const CommercialKitchenFlooring = lazy(() => import("./pages/CommercialKitchenFlooring"));
const AirplaneHangarFlooring = lazy(() => import("./pages/AirplaneHangarFlooring"));
const CondoBalconyWaterproofing = lazy(() => import("./pages/CondoBalconyWaterproofing"));
const CoatingRemoval = lazy(() => import("./pages/CoatingRemoval"));
const CommercialFloorCoatingsBradenton = lazy(() => import("./pages/CommercialFloorCoatingsBradenton"));
const HOACondoFloorCoatings = lazy(() => import("./pages/HOACondoFloorCoatings"));
const GarageFloorCoatingLakewoodRanch = lazy(() => import("./pages/GarageFloorCoatingLakewoodRanch"));
const PoolDeckResurfacingBradenton = lazy(() => import("./pages/PoolDeckResurfacingBradenton"));
const MetallicEpoxySarasota = lazy(() => import("./pages/MetallicEpoxySarasota"));
const GeneralContractorFlooring = lazy(() => import("./pages/GeneralContractorFlooring"));
const PropertyManagementFlooring = lazy(() => import("./pages/PropertyManagementFlooring"));
const WarehouseFlooring = lazy(() => import("./pages/WarehouseFlooring"));
const RestaurantFlooring = lazy(() => import("./pages/RestaurantFlooring"));
const GarageFloorCoatingSarasota = lazy(() => import("./pages/GarageFloorCoatingSarasota"));
const GarageFloorCoatingVenice = lazy(() => import("./pages/GarageFloorCoatingVenice"));
const GarageFloorCoatingNorthPort = lazy(() => import("./pages/GarageFloorCoatingNorthPort"));
const GarageFloorCoatingPortCharlotte = lazy(() => import("./pages/GarageFloorCoatingPortCharlotte"));
const GarageFloorCoatingPuntaGorda = lazy(() => import("./pages/GarageFloorCoatingPuntaGorda"));
const GarageFloorCoatingRotondaWest = lazy(() => import("./pages/GarageFloorCoatingRotondaWest"));
const PoolDeckResurfacingSarasota = lazy(() => import("./pages/PoolDeckResurfacingSarasota"));
const PoolDeckResurfacingVenice = lazy(() => import("./pages/PoolDeckResurfacingVenice"));
const PoolDeckResurfacingNorthPort = lazy(() => import("./pages/PoolDeckResurfacingNorthPort"));
const PoolDeckResurfacingPortCharlotte = lazy(() => import("./pages/PoolDeckResurfacingPortCharlotte"));
const PoolDeckResurfacingPuntaGorda = lazy(() => import("./pages/PoolDeckResurfacingPuntaGorda"));
const PoolDeckResurfacingRotondaWest = lazy(() => import("./pages/PoolDeckResurfacingRotondaWest"));
const CommercialFloorCoatingsSarasota = lazy(() => import("./pages/CommercialFloorCoatingsSarasota"));
const CommercialFloorCoatingsVenice = lazy(() => import("./pages/CommercialFloorCoatingsVenice"));
const CommercialFloorCoatingsNorthPort = lazy(() => import("./pages/CommercialFloorCoatingsNorthPort"));
const CommercialFloorCoatingsPortCharlotte = lazy(() => import("./pages/CommercialFloorCoatingsPortCharlotte"));
const CommercialFloorCoatingsPuntaGorda = lazy(() => import("./pages/CommercialFloorCoatingsPuntaGorda"));
const CommercialFloorCoatingsRotondaWest = lazy(() => import("./pages/CommercialFloorCoatingsRotondaWest"));
const GarageFloorCoatingFortMyers = lazy(() => import("./pages/GarageFloorCoatingFortMyers"));
const GarageFloorCoatingCapeCoral = lazy(() => import("./pages/GarageFloorCoatingCapeCoral"));
const GarageFloorCoatingBonitaSprings = lazy(() => import("./pages/GarageFloorCoatingBonitaSprings"));
const GarageFloorCoatingEstero = lazy(() => import("./pages/GarageFloorCoatingEstero"));
const GarageFloorCoatingMarcoIsland = lazy(() => import("./pages/GarageFloorCoatingMarcoIsland"));
const PoolDeckResurfacingFortMyers = lazy(() => import("./pages/PoolDeckResurfacingFortMyers"));
const PoolDeckResurfacingCapeCoral = lazy(() => import("./pages/PoolDeckResurfacingCapeCoral"));
const PoolDeckResurfacingBonitaSprings = lazy(() => import("./pages/PoolDeckResurfacingBonitaSprings"));
const PoolDeckResurfacingEstero = lazy(() => import("./pages/PoolDeckResurfacingEstero"));
const PoolDeckResurfacingMarcoIsland = lazy(() => import("./pages/PoolDeckResurfacingMarcoIsland"));
const CommercialFloorCoatingsFortMyers = lazy(() => import("./pages/CommercialFloorCoatingsFortMyers"));
const CommercialFloorCoatingsCapeCoral = lazy(() => import("./pages/CommercialFloorCoatingsCapeCoral"));
const CommercialFloorCoatingsBonitaSprings = lazy(() => import("./pages/CommercialFloorCoatingsBonitaSprings"));
const CommercialFloorCoatingsEstero = lazy(() => import("./pages/CommercialFloorCoatingsEstero"));
const CommercialFloorCoatingsMarcoIsland = lazy(() => import("./pages/CommercialFloorCoatingsMarcoIsland"));
const CostCalculator = lazy(() => import("./pages/CostCalculator"));
const AIFloorVisualizer = lazy(() => import("./pages/AIFloorVisualizer"));
const PolyasparticFloorCoating = lazy(() => import("./pages/PolyasparticFloorCoating"));
const EpoxyFloorInstallationCost = lazy(() => import("./pages/EpoxyFloorInstallationCost"));
const EpoxyFloorInstallers = lazy(() => import("./pages/EpoxyFloorInstallers"));
const Naples3D = lazy(() => import("./pages/lab/Naples3D"));
const AdminStats = lazy(() => import("./pages/AdminStats"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Minimal loading state to avoid CLS / flash
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white" aria-label="Loading page">
    <div className="w-12 h-12 border-4 border-brand-gold border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  useEffect(() => {
    initAttribution();
    registerWebMCPTools();
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (href.startsWith('tel:')) track('phone_click', { href });
      else if (href.includes('wa.me') || href.includes('whatsapp')) track('whatsapp_click', { href });
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999] focus:bg-brand-gold focus:text-brand-black focus:px-4 focus:py-2 focus:rounded focus:font-bold">Skip to main content</a>
        <main id="main-content" role="main">
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ========== CANONICAL ROUTES (sin .html) ========== */}
          
          {/* Home — eager-loaded for fastest LCP on landing */}
          <Route path="/" element={<Home />} />
          
          {/* Main Pages - CANONICAL */}
          <Route path="/services" element={<Services />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/showroom" element={<Showroom />} />
          <Route path="/products" element={<Products />} />
          <Route path="/visualizer" element={<FloorVisualizer />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/why-epoxyart33" element={<WhyEpoxyArt33 />} />
          
          {/* Service Pages - CANONICAL (SEO-optimized URLs) */}
          <Route path="/garage-floor-coating" element={<GarageFloorCoating />} />
          <Route path="/garage-flake-floors-naples-fl" element={<FlakeSystem />} />
          <Route path="/metallic-epoxy-floors-naples-fl" element={<MetallicEpoxy />} />
          <Route path="/quartz-pool-deck-coatings-naples-fl" element={<QuartzSystem />} />
          <Route path="/commercial-epoxy-flooring-naples-fl" element={<CommercialEpoxy />} />
          <Route path="/urethane-cement-flooring-naples-fl" element={<UrethaneCement />} />
          <Route path="/pool-deck-resurfacing" element={<PoolDeckResurfacing />} />
          <Route path="/industrial-epoxy" element={<IndustrialEpoxy />} />
          <Route path="/concrete-repair" element={<ConcreteRepair />} />
          <Route path="/driveway-resurfacing" element={<DrivewayResurfacing />} />
          <Route path="/waterproofing" element={<Waterproofing />} />
          <Route path="/concrete-polishing-naples-fl" element={<ConcretePolishing />} />
          <Route path="/warranty" element={<Warranty />} />
          <Route path="/service-areas" element={<ServiceAreas />} />
          <Route path="/residential-concrete-coatings" element={<ResidentialHub />} />
          <Route path="/commercial-floor-coatings" element={<CommercialHub />} />
          <Route path="/concrete-flooring-systems" element={<SystemsHub />} />
          <Route path="/industries" element={<IndustriesHub />} />
          <Route path="/epoxy-flooring-lakewood-ranch-bradenton" element={<EpoxyFlooringLakewoodRanch />} />
          <Route path="/garage-floor-coating-lakewood-ranch" element={<GarageFloorCoatingLakewoodRanch />} />
          <Route path="/pool-deck-resurfacing-bradenton" element={<PoolDeckResurfacingBradenton />} />
          <Route path="/metallic-epoxy-sarasota" element={<MetallicEpoxySarasota />} />
          <Route path="/commercial-kitchen-flooring-naples-fl" element={<CommercialKitchenFlooring />} />
          <Route path="/airplane-hangar-floor-coatings-florida" element={<AirplaneHangarFlooring />} />
          <Route path="/condo-balcony-waterproofing-naples-fl" element={<CondoBalconyWaterproofing />} />
          <Route path="/concrete-coating-removal-naples-fl" element={<CoatingRemoval />} />
          <Route path="/commercial-floor-coatings-bradenton-fl" element={<CommercialFloorCoatingsBradenton />} />
          <Route path="/hoa-condo-floor-coatings-sarasota-manatee" element={<HOACondoFloorCoatings />} />
          <Route path="/general-contractor-flooring-southwest-florida" element={<GeneralContractorFlooring />} />
          <Route path="/property-management-flooring-southwest-florida" element={<PropertyManagementFlooring />} />
          <Route path="/warehouse-floor-coatings-southwest-florida" element={<WarehouseFlooring />} />
          <Route path="/restaurant-flooring-southwest-florida" element={<RestaurantFlooring />} />
          <Route path="/garage-floor-coating-sarasota" element={<GarageFloorCoatingSarasota />} />
          <Route path="/garage-floor-coating-venice" element={<GarageFloorCoatingVenice />} />
          <Route path="/garage-floor-coating-north-port" element={<GarageFloorCoatingNorthPort />} />
          <Route path="/garage-floor-coating-port-charlotte" element={<GarageFloorCoatingPortCharlotte />} />
          <Route path="/garage-floor-coating-punta-gorda" element={<GarageFloorCoatingPuntaGorda />} />
          <Route path="/garage-floor-coating-rotonda-west" element={<GarageFloorCoatingRotondaWest />} />
          <Route path="/pool-deck-resurfacing-sarasota" element={<PoolDeckResurfacingSarasota />} />
          <Route path="/pool-deck-resurfacing-venice" element={<PoolDeckResurfacingVenice />} />
          <Route path="/pool-deck-resurfacing-north-port" element={<PoolDeckResurfacingNorthPort />} />
          <Route path="/pool-deck-resurfacing-port-charlotte" element={<PoolDeckResurfacingPortCharlotte />} />
          <Route path="/pool-deck-resurfacing-punta-gorda" element={<PoolDeckResurfacingPuntaGorda />} />
          <Route path="/pool-deck-resurfacing-rotonda-west" element={<PoolDeckResurfacingRotondaWest />} />
          <Route path="/commercial-floor-coatings-sarasota-fl" element={<CommercialFloorCoatingsSarasota />} />
          <Route path="/commercial-floor-coatings-venice-fl" element={<CommercialFloorCoatingsVenice />} />
          <Route path="/commercial-floor-coatings-north-port-fl" element={<CommercialFloorCoatingsNorthPort />} />
          <Route path="/commercial-floor-coatings-port-charlotte-fl" element={<CommercialFloorCoatingsPortCharlotte />} />
          <Route path="/commercial-floor-coatings-punta-gorda-fl" element={<CommercialFloorCoatingsPuntaGorda />} />
          <Route path="/commercial-floor-coatings-rotonda-west-fl" element={<CommercialFloorCoatingsRotondaWest />} />
          <Route path="/garage-floor-coating-fort-myers" element={<GarageFloorCoatingFortMyers />} />
          <Route path="/garage-floor-coating-cape-coral" element={<GarageFloorCoatingCapeCoral />} />
          <Route path="/garage-floor-coating-bonita-springs" element={<GarageFloorCoatingBonitaSprings />} />
          <Route path="/garage-floor-coating-estero" element={<GarageFloorCoatingEstero />} />
          <Route path="/garage-floor-coating-marco-island" element={<GarageFloorCoatingMarcoIsland />} />
          <Route path="/pool-deck-resurfacing-fort-myers" element={<PoolDeckResurfacingFortMyers />} />
          <Route path="/pool-deck-resurfacing-cape-coral" element={<PoolDeckResurfacingCapeCoral />} />
          <Route path="/pool-deck-resurfacing-bonita-springs" element={<PoolDeckResurfacingBonitaSprings />} />
          <Route path="/pool-deck-resurfacing-estero" element={<PoolDeckResurfacingEstero />} />
          <Route path="/pool-deck-resurfacing-marco-island" element={<PoolDeckResurfacingMarcoIsland />} />
          <Route path="/commercial-floor-coatings-fort-myers-fl" element={<CommercialFloorCoatingsFortMyers />} />
          <Route path="/commercial-floor-coatings-cape-coral-fl" element={<CommercialFloorCoatingsCapeCoral />} />
          <Route path="/commercial-floor-coatings-bonita-springs-fl" element={<CommercialFloorCoatingsBonitaSprings />} />
          <Route path="/commercial-floor-coatings-estero-fl" element={<CommercialFloorCoatingsEstero />} />
          <Route path="/commercial-floor-coatings-marco-island-fl" element={<CommercialFloorCoatingsMarcoIsland />} />
          <Route path="/admin-stats" element={<AdminStats />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
          <Route path="/government-contracts" element={<GovernmentContracts />} />
          <Route path="/epoxy-vs-polyaspartic" element={<EpoxyVsPolyaspartic />} />
          
          {/* City Landing Pages - CANONICAL */}
          <Route path="/epoxy-flooring-naples" element={<EpoxyFlooringNaples />} />
          <Route path="/epoxy-flooring-fort-myers" element={<EpoxyFlooringFortMyers />} />
          <Route path="/epoxy-flooring-cape-coral" element={<EpoxyFlooringCapeCoral />} />
          <Route path="/epoxy-flooring-sarasota" element={<EpoxyFlooringSarasota />} />
          <Route path="/epoxy-flooring-venice" element={<EpoxyFlooringVenice />} />
          <Route path="/epoxy-flooring-north-port" element={<EpoxyFlooringNorthPort />} />
          <Route path="/epoxy-flooring-port-charlotte" element={<EpoxyFlooringPortCharlotte />} />
          <Route path="/epoxy-flooring-punta-gorda" element={<EpoxyFlooringPuntaGorda />} />
          <Route path="/epoxy-flooring-rotonda-west" element={<EpoxyFlooringRotondaWest />} />
          <Route path="/epoxy-flooring-marco-island" element={<EpoxyFlooringMarcoIsland />} />
          <Route path="/epoxy-flooring-bonita-springs" element={<EpoxyFlooringBonitaSprings />} />
          <Route path="/epoxy-flooring-estero" element={<EpoxyFlooringEstero />} />
          
          {/* ========== 301 REDIRECTS - .html → canonical ========== */}
          
          {/* Main pages .html redirects */}
          <Route path="/services.html" element={<Navigate to="/services" replace />} />
          <Route path="/gallery.html" element={<Navigate to="/gallery" replace />} />
          <Route path="/contact.html" element={<Navigate to="/contact" replace />} />
          <Route path="/blog.html" element={<Navigate to="/blog" replace />} />
          <Route path="/blog/:slug.html" element={<Navigate to="/blog/:slug" replace />} />
          <Route path="/faq.html" element={<Navigate to="/faq" replace />} />
          <Route path="/showroom.html" element={<Navigate to="/showroom" replace />} />
          <Route path="/products.html" element={<Navigate to="/products" replace />} />
          <Route path="/visualizer.html" element={<Navigate to="/visualizer" replace />} />
          <Route path="/privacy.html" element={<Navigate to="/privacy" replace />} />
          <Route path="/why-epoxyart33.html" element={<Navigate to="/why-epoxyart33" replace />} />
          
          {/* Service pages - redirect old URLs to canonical */}
          <Route path="/garage-floor-coating.html" element={<Navigate to="/garage-floor-coating" replace />} />
          <Route path="/flake-system" element={<Navigate to="/garage-flake-floors-naples-fl" replace />} />
          <Route path="/flake-system.html" element={<Navigate to="/garage-flake-floors-naples-fl" replace />} />
          <Route path="/garage-flake-floors-naples-fl.html" element={<Navigate to="/garage-flake-floors-naples-fl" replace />} />
          <Route path="/metallic-epoxy" element={<Navigate to="/metallic-epoxy-floors-naples-fl" replace />} />
          <Route path="/metallic-epoxy.html" element={<Navigate to="/metallic-epoxy-floors-naples-fl" replace />} />
          <Route path="/metallic-epoxy-floors-naples-fl.html" element={<Navigate to="/metallic-epoxy-floors-naples-fl" replace />} />
          <Route path="/quartz-system" element={<Navigate to="/quartz-pool-deck-coatings-naples-fl" replace />} />
          <Route path="/quartz-system.html" element={<Navigate to="/quartz-pool-deck-coatings-naples-fl" replace />} />
          <Route path="/quartz-pool-deck-coatings-naples-fl.html" element={<Navigate to="/quartz-pool-deck-coatings-naples-fl" replace />} />
          <Route path="/commercial-epoxy" element={<Navigate to="/commercial-epoxy-flooring-naples-fl" replace />} />
          <Route path="/commercial-epoxy.html" element={<Navigate to="/commercial-epoxy-flooring-naples-fl" replace />} />
          <Route path="/commercial-epoxy-flooring-naples-fl.html" element={<Navigate to="/commercial-epoxy-flooring-naples-fl" replace />} />
          <Route path="/urethane-cement" element={<Navigate to="/urethane-cement-flooring-naples-fl" replace />} />
          <Route path="/urethane-cement.html" element={<Navigate to="/urethane-cement-flooring-naples-fl" replace />} />
          <Route path="/urethane-cement-flooring-naples-fl.html" element={<Navigate to="/urethane-cement-flooring-naples-fl" replace />} />
          <Route path="/pool-deck-resurfacing.html" element={<Navigate to="/pool-deck-resurfacing" replace />} />
          <Route path="/industrial-epoxy.html" element={<Navigate to="/industrial-epoxy" replace />} />
          <Route path="/concrete-repair.html" element={<Navigate to="/concrete-repair" replace />} />
          <Route path="/driveway-resurfacing.html" element={<Navigate to="/driveway-resurfacing" replace />} />
          <Route path="/waterproofing.html" element={<Navigate to="/waterproofing" replace />} />
          <Route path="/government-contracts.html" element={<Navigate to="/government-contracts" replace />} />
          <Route path="/epoxy-vs-polyaspartic.html" element={<Navigate to="/epoxy-vs-polyaspartic" replace />} />
          
          {/* City pages .html redirects */}
          <Route path="/epoxy-flooring-naples.html" element={<Navigate to="/epoxy-flooring-naples" replace />} />
          <Route path="/epoxy-flooring-fort-myers.html" element={<Navigate to="/epoxy-flooring-fort-myers" replace />} />
          <Route path="/epoxy-flooring-cape-coral.html" element={<Navigate to="/epoxy-flooring-cape-coral" replace />} />
          <Route path="/epoxy-flooring-marco-island.html" element={<Navigate to="/epoxy-flooring-marco-island" replace />} />
          <Route path="/epoxy-flooring-bonita-springs.html" element={<Navigate to="/epoxy-flooring-bonita-springs" replace />} />
          <Route path="/epoxy-flooring-estero.html" element={<Navigate to="/epoxy-flooring-estero" replace />} />
          
          <Route path="/cost-calculator" element={<CostCalculator />} />
          <Route path="/ai-visualizer" element={<AIFloorVisualizer />} />
          <Route path="/polyaspartic-floor-coating-naples-fl" element={<PolyasparticFloorCoating />} />
          <Route path="/epoxy-floor-installation-cost-naples-fl" element={<EpoxyFloorInstallationCost />} />
          <Route path="/epoxy-floor-installers-naples-fl" element={<EpoxyFloorInstallers />} />
          <Route path="/lab/naples-3d" element={<Naples3D />} />

          {/* Legacy short routes */}
          <Route path="/why" element={<Navigate to="/why-epoxyart33" replace />} />

          {/* GSC Soft-404 fixes: legacy URLs from previous site versions */}
          <Route path="/index.html" element={<Navigate to="/" replace />} />
          <Route path="/es" element={<Navigate to="/" replace />} />
          <Route path="/es/*" element={<Navigate to="/" replace />} />
          <Route path="/en" element={<Navigate to="/" replace />} />
          <Route path="/en/*" element={<Navigate to="/" replace />} />
          <Route path="/EpoxyArt33Tiling" element={<Navigate to="/" replace />} />

          {/* 404 catch-all — must stay LAST */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </main>
        <ChatWidget />
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
